import React from 'react';
import { Tooltip } from 'antd';
import { palette, compact, money, prettyMonth } from './theme';
import { monthValue, isBoth } from './scope';

// Small marks drawn in plain HTML/SVG. No charting library: three shapes do not
// justify a dependency, and the bundle is served from a VPS with a $10/mo
// ceiling on the whole stack.
//
// Mark specs follow the dataviz skill: bars capped at 24px, 4px rounded
// data-end, 2px surface gaps between touching segments, hairline baselines,
// selective direct labels (never a value on every mark), and text in ink
// tokens rather than the series colour.

const GAP = 2;

/**
 * Horizontal part-to-whole strip.
 *
 * Segments narrower than the gap are dropped rather than drawn as slivers — a
 * 0.4px band is not readable, and rendering it only adds a seam. The value is
 * still in the tooltip and in the drill-down table, so nothing is hidden.
 */
export function StackedStrip({ segments, height = 10, ariaLabel }) {
  const total = segments.reduce((s, x) => s + Math.abs(Number(x.value ?? 0)), 0);
  if (total === 0) {
    return (
      <div
        role="img"
        aria-label={`${ariaLabel}: nothing to show`}
        style={{ height, borderRadius: 4, background: palette.gridline }}
      />
    );
  }

  const visible = segments.filter((s) => Math.abs(Number(s.value ?? 0)) / total > 0.005);

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={{ display: 'flex', gap: GAP, height, alignItems: 'stretch' }}
    >
      {visible.map((s, i) => (
        <Tooltip key={s.label} title={`${s.label}: ${money(s.value)}`}>
          <div
            style={{
              flex: `${Math.abs(Number(s.value ?? 0)) / total} 1 0`,
              background: s.color,
              // Round only the outer ends so the strip reads as one bar.
              borderTopLeftRadius: i === 0 ? 4 : 0,
              borderBottomLeftRadius: i === 0 ? 4 : 0,
              borderTopRightRadius: i === visible.length - 1 ? 4 : 0,
              borderBottomRightRadius: i === visible.length - 1 ? 4 : 0,
              minWidth: 3,
            }}
          />
        </Tooltip>
      ))}
    </div>
  );
}

/**
 * Builds the aging strip's segments in bucket order, oldest last.
 *
 * Any advance credit is absorbed from the oldest bucket backwards before the
 * segments are drawn. That is not cosmetic: on the payables side RBS has
 * Rs 37.6M of trade payables against Rs 33.4M of supplier advances, so a strip
 * drawn from the raw buckets would depict a Rs 37.6M balance sitting directly
 * under a tile reading Rs 4.2M. A part-to-whole bar that adds up to something
 * other than the number above it is worse than no bar.
 *
 * Oldest-first is the same rule the backend uses for `overdue60Plus`, so the
 * strip and the sentence under it always tell the same story — after
 * absorption RBS's strip and its "Rs 475K unpaid over 60 days" agree exactly.
 */
export function agingSegments(aging) {
  if (!aging) return [];

  const buckets = [
    Number(aging.current ?? 0),
    Number(aging.days31to60 ?? 0),
    Number(aging.days61to90 ?? 0),
    Number(aging.days91to120 ?? 0),
    Number(aging.daysOver120 ?? 0),
  ];
  let opening = Number(aging.openingBalances ?? 0);

  // `advances` is zero or negative. Walk oldest → newest, then opening last:
  // advances offset trade before they offset a go-live balance.
  let credit = -Number(aging.advances ?? 0);
  for (let i = buckets.length - 1; i >= 0 && credit > 0; i -= 1) {
    const absorbed = Math.min(buckets[i], credit);
    buckets[i] -= absorbed;
    credit -= absorbed;
  }
  if (credit > 0) {
    opening = Math.max(opening - credit, 0);
  }

  const segs = buckets.map((value, i) => ({
    label: palette.agingLabels[i],
    value,
    color: palette.aging[i],
  }));
  // Opening balances sit outside the ordered ramp, in grey, at the end — they
  // are a go-live artefact rather than an age. See the backend's Aging DTO.
  segs.push({
    label: 'Opening balances (not aged)',
    value: opening,
    color: palette.opening,
  });
  return segs;
}

/**
 * Monthly columns. One series, so no legend — the heading says what is plotted.
 *
 * The current month is drawn in a lighter step of the same hue and labelled
 * "so far", because it is month-to-date and comparing a partial month with
 * complete ones as though they were alike is the most common way a sales chart
 * lies.
 */
export function MonthlyColumns({ points, scope, height = 96 }) {
  if (!points || points.length === 0) return null;

  const valueOf = (p) => Number(monthValue(p, scope) ?? 0);
  // Scale to the scoped series, not the group: under "SBY" every group-scaled
  // bar would be invisible, and an all-but-flat chart looks like a bug rather
  // than like a company that does not invoice.
  const max = Math.max(...points.map(valueOf), 1);
  const lastIndex = points.length - 1;

  // Each month gets a fixed 56px slot with a 24px bar centred in it, and the
  // whole chart is capped at that width. Letting the slots stretch to fill the
  // card would leave four narrow bars huddled against a baseline running the
  // full width — which reads as a broken render rather than as a short series.
  const SLOT = 56;
  // Room for the cap labels only while they cannot collide; past that the
  // tooltip carries the values, per "never a number on every mark".
  const showCapLabels = points.length <= 6;

  return (
    <div>
    <div style={{ maxWidth: points.length * SLOT }}>
      {showCapLabels && (
        <div style={{ display: 'flex', marginBottom: 4 }}>
          {points.map((p) => (
            <div key={p.month} style={{ flex: '1 1 0', textAlign: 'center', fontSize: 11,
                                        color: palette.inkSecondary,
                                        fontVariantNumeric: 'tabular-nums' }}>
              {valueOf(p) > 0 ? compact(valueOf(p)).replace('Rs ', '') : ''}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', height,
                    borderBottom: `1px solid ${palette.baseline}` }}>
        {points.map((p, i) => {
          const value = valueOf(p);
          const inProgress = i === lastIndex;
          return (
            <Tooltip
              key={p.month}
              title={
                <span>
                  {prettyMonth(p.month)}{inProgress ? ' (so far)' : ''}<br />
                  Net {money(valueOf(p))}
                  {isBoth(scope) && (
                    <>
                      <br />RBS {money(p.rbsNet)}
                      <br />SBY {money(p.sbyNet)}
                    </>
                  )}
                </span>
              }
            >
              <div style={{ flex: '1 1 0', display: 'flex', alignItems: 'flex-end',
                            justifyContent: 'center', height: '100%', cursor: 'default' }}>
                <div
                  style={{
                    width: 24,
                    height: `${Math.max((value / max) * 100, value > 0 ? 2 : 0)}%`,
                    background: inProgress ? palette.seriesInProgress : palette.series,
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                  }}
                />
              </div>
            </Tooltip>
          );
        })}
      </div>

      <div style={{ display: 'flex', marginTop: 6 }}>
        {points.map((p, i) => (
          <div
            key={p.month}
            style={{
              flex: '1 1 0', textAlign: 'center', fontSize: 11, whiteSpace: 'nowrap',
              color: i === lastIndex ? palette.inkSecondary : palette.inkMuted,
            }}
          >
            {prettyMonth(p.month).split(' ')[0]}
          </div>
        ))}
      </div>

    </div>

      {/* Sits outside the width-capped chart so it does not wrap. The last
          column is month-to-date, and saying so is the difference between a
          chart that informs and one that quietly implies this month is down. */}
      <div style={{ fontSize: 11, color: palette.inkMuted, marginTop: 6 }}>
        Net sales by month · last column is month to date
      </div>
    </div>
  );
}

/**
 * Per-store stock, directly labelled.
 *
 * The labels are not optional decoration: aqua sits below 3:1 against a white
 * surface, and the validator's relief rule requires visible labels wherever it
 * is used. Identity never rests on the swatch alone.
 */
export function StoreBars({ stores }) {
  const rows = (stores || []).filter((s) => Number(s.value ?? 0) !== 0);
  if (rows.length === 0) {
    return <div style={{ color: palette.inkMuted, fontSize: 13 }}>No stock recorded</div>;
  }
  const max = Math.max(...rows.map((s) => Number(s.value)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((s) => {
        const storeIndex = ['DEFAULT', 'PRODUCTION', 'DAMAGE'].indexOf(s.storeName);
        const color = palette.stores[storeIndex >= 0 ? storeIndex : 0];
        return (
          <div key={`${s.company}-${s.storeName}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: palette.inkSecondary }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                               background: color, marginRight: 6 }} />
                {s.company} · {s.storeName}
              </span>
              <Tooltip title={money(s.value)}>
                <span style={{ color: palette.ink, fontVariantNumeric: 'tabular-nums' }}>
                  {compact(s.value)}
                </span>
              </Tooltip>
            </div>
            <div style={{ height: 8, background: palette.plane, borderRadius: 4 }}>
              <div style={{ width: `${(Number(s.value) / max) * 100}%`, height: '100%',
                            background: color, borderRadius: 4 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Small labelled key. Used in the drawers, where there is room to be explicit. */
export function Legend({ items }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12,
                  color: palette.inkSecondary }}>
      {items.map((it) => (
        <span key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}
